import { ALL_MATCHES, STYLED_MATCHES, makeRule, makeRawRule } from "../stylemap.js";

export const SCHEMA_VERSION = 1;
export const SAMPLES = ["title", "heading", "paragraph", "quote", "callout", "code", "caption", "inline", "list", "table"];
export const PRIORITIES = ["first", "last"];
const ELEMENT = "[a-z][a-z0-9-]*(?:\\.[A-Za-z0-9_-]+)*";
export const WRAPPER_RE = new RegExp(`^${ELEMENT}(?:\\s*>\\s*${ELEMENT})*$`);

export function slugify(name) {
  return String(name).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "profile";
}

function normaliseRule(input, index, errors) {
  const where = `Rule ${index + 1}`;
  if (!input || typeof input !== "object" || Array.isArray(input)) {
    errors.push(`${where}: not a rule object; skipped.`);
    return null;
  }
  if (typeof input.raw === "string") {
    return makeRawRule(input.raw, typeof input.error === "string" ? input.error : undefined);
  }
  if (!ALL_MATCHES.includes(input.match)) {
    errors.push(`${where}: unknown type "${input.match}"; skipped.`);
    return null;
  }
  if (input.html !== undefined && typeof input.html !== "string") {
    errors.push(`${where}: html must be text; skipped.`);
    return null;
  }
  if (input.priority !== undefined && !PRIORITIES.includes(input.priority)) {
    errors.push(`${where}: priority must be "first" or "last"; ignored.`);
  }
  // Legacy "word" settings (Word appearance) are ignored on purpose.
  return makeRule({
    match: input.match,
    style: STYLED_MATCHES.includes(input.match) && typeof input.style === "string" ? input.style.trim() : undefined,
    prefix: input.prefix === true,
    html: (input.html ?? "").trim(),
    group: input.group === "quiet" ? "quiet" : "main",
    sample: SAMPLES.includes(input.sample) ? input.sample : undefined,
    priority: PRIORITIES.includes(input.priority) ? input.priority : undefined,
  });
}

const text = (value) => (typeof value === "string" ? value : "");
const textList = (value) =>
  Array.isArray(value) ? value.filter((v) => typeof v === "string" && v.trim()).map((v) => v.trim()) : [];

function wrapperField(value, label, errors) {
  const wrapper = text(value).trim();
  if (wrapper && !WRAPPER_RE.test(wrapper)) {
    errors.push(`${label} "${wrapper}" isn't a valid wrapper (use e.g. div.content); ignored.`);
    return "";
  }
  return wrapper;
}

export function normaliseProfile(input) {
  if (!input || typeof input !== "object" || Array.isArray(input)) {
    return { profile: null, errors: ["A profile must be a JSON object."] };
  }
  const errors = [];
  if (input.schema !== SCHEMA_VERSION) errors.push(`Unsupported schema version ${input.schema ?? "(missing)"}; expected ${SCHEMA_VERSION}.`);
  if (typeof input.name !== "string" || !input.name.trim()) errors.push("The profile needs a name.");
  if (!Array.isArray(input.rules)) errors.push("The profile needs a rules list.");
  if (errors.length) return { profile: null, errors };

  const profile = {
    schema: SCHEMA_VERSION,
    id: text(input.id).trim() || slugify(input.name),
    name: input.name.trim(),
    builtIn: input.builtIn === true,
    basedOn: text(input.basedOn),
    description: text(input.description),
    css: textList(input.css),
    exportCss: textList(input.exportCss),
    extraCss: text(input.extraCss),
    wrapper: wrapperField(input.wrapper, "Wrapper", errors),
    exportWrapper: wrapperField(input.exportWrapper, "Full-page wrapper", errors),
    rules: input.rules.map((rule, i) => normaliseRule(rule, i, errors)).filter(Boolean),
  };
  return { profile, errors };
}
